<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import ContactsAPI from 'dashboard/api/contacts';

const props = defineProps({
  show: { type: Boolean, required: true },
  initialCard: { type: Object, default: () => ({}) },
  stageId: { type: Number, default: null },
});
const emit = defineEmits(['close', 'submit', 'delete']);

const form = ref({
  title: '',
  description: '',
  contact_id: null,
  stage_id: null,
  due_at: '',
});

const selectedContactLabel = ref('');
const contactSearch = ref('');
const searchResults = ref([]);
const searching = ref(false);

// Quick-create contact
const showQuickCreate = ref(false);
const quickContact = ref({ name: '', phone_number: '', email: '' });
const quickCreateError = ref('');
const quickCreating = ref(false);

watch(
  () => [props.show, props.initialCard, props.stageId],
  () => {
    if (!props.show) return;
    const c = props.initialCard || {};
    form.value = {
      title: c.title || '',
      description: c.description || '',
      contact_id: c.contact?.id || null,
      stage_id: c.stage_id || props.stageId,
      due_at: c.due_at ? c.due_at.slice(0, 16) : '',
    };
    selectedContactLabel.value = c.contact
      ? `${c.contact.name}${c.contact.phone_number ? ' · ' + c.contact.phone_number : ''}`
      : '';
    contactSearch.value = '';
    searchResults.value = [];
    showQuickCreate.value = false;
    quickContact.value = { name: '', phone_number: '', email: '' };
    quickCreateError.value = '';
  },
  { immediate: true }
);

let searchAbort = null;
async function runSearch() {
  const q = contactSearch.value.trim();
  if (q.length < 2) {
    searchResults.value = [];
    return;
  }
  searchAbort?.abort?.();
  const ctrl = new AbortController();
  searchAbort = ctrl;
  searching.value = true;
  try {
    const { data } = await ContactsAPI.search(q, 1, 'name', '', { signal: ctrl.signal });
    if (!ctrl.signal.aborted) {
      searchResults.value = data?.payload || [];
    }
  } catch (e) {
    if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
      searchResults.value = [];
    }
  } finally {
    if (searchAbort === ctrl) searching.value = false;
  }
}

let debounce = null;
watch(contactSearch, () => {
  clearTimeout(debounce);
  debounce = setTimeout(runSearch, 250);
});

function pickContact(contact) {
  form.value.contact_id = contact.id;
  selectedContactLabel.value = `${contact.name}${contact.phone_number ? ' · ' + contact.phone_number : ''}`;
  searchResults.value = [];
  contactSearch.value = '';
}

function clearContact() {
  form.value.contact_id = null;
  selectedContactLabel.value = '';
}

async function submitQuickCreate() {
  quickCreateError.value = '';
  const payload = {
    name: quickContact.value.name.trim(),
    phone_number: quickContact.value.phone_number.trim(),
    email: quickContact.value.email.trim(),
  };
  if (!payload.name) {
    quickCreateError.value = 'Nome é obrigatório';
    return;
  }
  if (!payload.phone_number && !payload.email) {
    quickCreateError.value = 'Informe telefone ou e-mail';
    return;
  }
  quickCreating.value = true;
  try {
    const { data } = await ContactsAPI.create(payload);
    const created = data?.payload?.contact || data?.contact || data;
    pickContact(created);
    showQuickCreate.value = false;
  } catch (e) {
    const msg = e?.response?.data?.message || 'Falha ao criar contato';
    quickCreateError.value = msg;
  } finally {
    quickCreating.value = false;
  }
}

const canSubmit = computed(
  () => form.value.title.trim().length > 0 && form.value.contact_id
);

function submit() {
  if (!canSubmit.value) return;
  emit('submit', {
    title: form.value.title.trim(),
    description: form.value.description.trim(),
    contact_id: form.value.contact_id,
    stage_id: form.value.stage_id,
    due_at: form.value.due_at || null,
  });
}

function confirmDelete() {
  if (confirm('Excluir este card? A ação não pode ser desfeita.')) {
    emit('delete', props.initialCard?.id);
  }
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-n-solid-1 rounded-xl border border-n-weak w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto">
      <header class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ initialCard?.id ? 'Editar card' : 'Novo card' }}
        </h2>
        <button class="size-7 grid place-content-center rounded hover:bg-n-alpha-1 text-n-slate-10" @click="$emit('close')">
          <span class="i-lucide-x size-4" />
        </button>
      </header>

      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">Título *</label>
        <input
          v-model="form.title"
          class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-sm text-n-slate-12"
          placeholder="Ex.: Demonstração para Maria"
          maxlength="200"
        />
      </div>

      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">Contato *</label>
        <div v-if="form.contact_id" class="flex items-center justify-between bg-n-brand/10 border border-n-brand/30 rounded px-3 py-2">
          <span class="text-sm text-n-slate-12 truncate">{{ selectedContactLabel }}</span>
          <button class="text-xs text-n-slate-11 hover:text-red-500" @click="clearContact">Trocar</button>
        </div>
        <template v-else>
          <input
            v-model="contactSearch"
            class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-sm text-n-slate-12"
            placeholder="Buscar por nome ou telefone…"
          />
          <ul v-if="searchResults.length" class="max-h-40 overflow-y-auto border border-n-weak rounded mt-1">
            <li
              v-for="c in searchResults.slice(0, 8)"
              :key="c.id"
              class="px-3 py-1.5 hover:bg-n-alpha-1 cursor-pointer text-sm text-n-slate-12 flex justify-between gap-2"
              @click="pickContact(c)"
            >
              <span class="truncate">{{ c.name }}</span>
              <span class="text-xs text-n-slate-10 flex-shrink-0">{{ c.phone_number || c.email }}</span>
            </li>
          </ul>
          <div v-else-if="contactSearch.length >= 2 && !searching" class="text-xs text-n-slate-10 italic px-1 py-1">
            Nenhum contato encontrado.
          </div>
          <button
            v-if="!showQuickCreate"
            class="text-xs text-n-brand hover:underline mt-1"
            @click="showQuickCreate = true; quickContact.name = contactSearch"
          >
            + Criar contato rápido
          </button>
          <div v-else class="border border-n-weak rounded p-2 space-y-2 mt-2">
            <input v-model="quickContact.name" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-transparent text-n-slate-12" placeholder="Nome *" />
            <input v-model="quickContact.phone_number" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-transparent text-n-slate-12" placeholder="Telefone (E.164: +55…)" />
            <input v-model="quickContact.email" type="email" class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-transparent text-n-slate-12" placeholder="E-mail (opcional)" />
            <p v-if="quickCreateError" class="text-xs text-red-500">{{ quickCreateError }}</p>
            <div class="flex gap-2">
              <button
                class="flex-1 px-2 py-1.5 rounded bg-n-brand text-white text-sm disabled:opacity-50"
                :disabled="quickCreating || !quickContact.name.trim()"
                @click="submitQuickCreate"
              >
                {{ quickCreating ? 'Salvando…' : 'Criar e selecionar' }}
              </button>
              <button class="px-2 py-1.5 rounded border border-n-weak text-sm text-n-slate-11" @click="showQuickCreate = false">Cancelar</button>
            </div>
          </div>
        </template>
      </div>

      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">Descrição</label>
        <textarea
          v-model="form.description"
          rows="3"
          class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-sm text-n-slate-12"
        />
      </div>

      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">Prazo</label>
        <input
          v-model="form.due_at"
          type="datetime-local"
          class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-sm text-n-slate-12"
        />
      </div>

      <footer class="flex items-center justify-between gap-2 pt-3 border-t border-n-weak">
        <button
          v-if="initialCard?.id"
          class="text-sm text-red-600 hover:underline"
          @click="confirmDelete"
        >
          Excluir card
        </button>
        <span v-else />
        <div class="flex gap-2">
          <button class="px-3 py-1.5 rounded border border-n-weak text-sm text-n-slate-11" @click="$emit('close')">
            Cancelar
          </button>
          <button
            class="px-3 py-1.5 rounded bg-n-brand text-white text-sm disabled:opacity-50"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ initialCard?.id ? 'Salvar' : 'Criar card' }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>
